import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../../components/Input';
import Dropdown from '../../../../components/Dropdown';
import { VALIDATION_FIELD_MAPPING } from '../../../../utils/appConstant';
import { ListFilters } from '../../../../interfaces/property-visit.interface';
import { getStartEndOfMonth } from '../../../../utils/methods';
import useDebounce from '../../../../hooks/useDebounce';
import { LookupOption } from '../../../../mappers/property-visit.mapper';

interface FiltersProps {
  visitTypes: LookupOption[];
  allVisitTypes: LookupOption[];
  userRoles: LookupOption[];
  allUserRoles: LookupOption[];
  visitedByOptions: LookupOption[];
  allVisitedByOptions: LookupOption[];
  visitStatuses: LookupOption[];
  allVisitStatuses: LookupOption[];
  properties: any[];
  allProperties: any[];
  managerOptions: any[];
  allManagerOptions: any[];
  dropdownSearchData: any;
  onDropdownSearchChange: (e: any, name: string) => void;
  initialFilters: ListFilters;
  isInspector: boolean;
  onApply: (filters: ListFilters) => void;
  onReset: () => void;
}

const Filters: React.FC<FiltersProps> = ({
  visitTypes, allVisitTypes,
  userRoles, allUserRoles,
  visitedByOptions, allVisitedByOptions,
  visitStatuses, allVisitStatuses,
  properties, allProperties,
  managerOptions, allManagerOptions,
  dropdownSearchData,
  onDropdownSearchChange,
  initialFilters,
  isInspector,
  onApply,
  onReset,
}) => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<ListFilters>(initialFilters);
  const debouncedSearchText = useDebounce(filters.searchText, 500);
  const defaultDates = useMemo(() => getStartEndOfMonth(), []);

  const isApplied =
    filters.searchText !== '' ||
    filters.propertyId !== '' ||
    filters.visitType !== '' ||
    filters.userRole !== '' ||
    filters.visitedBy !== '' ||
    filters.visitStatus !== '' ||
    filters.managedByUserId !== '' ||
    filters.startDate !== defaultDates.startDate ||
    filters.endDate !== defaultDates.endDate;

  const [filtersApplied, setFiltersApplied] = useState(isApplied);

  useEffect(() => {
    setFilters(initialFilters);
  }, [initialFilters]);

  useEffect(() => {
    const filtersToApply = { ...filters, searchText: debouncedSearchText };

    const applied =
      filtersToApply.searchText !== '' ||
      filtersToApply.propertyId !== '' ||
      filtersToApply.visitType !== '' ||
      filtersToApply.userRole !== '' ||
      filtersToApply.visitedBy !== '' ||
      filtersToApply.visitStatus !== '' ||
      filtersToApply.managedByUserId !== '' ||
      filtersToApply.startDate !== defaultDates.startDate ||
      filtersToApply.endDate !== defaultDates.endDate;

    setFiltersApplied(applied);
    onApply(filtersToApply);
  }, [
    debouncedSearchText,
    filters.propertyId,
    filters.visitType,
    filters.userRole,
    filters.visitedBy,
    filters.visitStatus,
    filters.managedByUserId,
    filters.startDate,
    filters.endDate,
    defaultDates,
    onApply,
  ]);

  const handleInputChange = useCallback((event: any, name: string) => {
    if (name === VALIDATION_FIELD_MAPPING.userRole) {
      // When role changes, reset visitedBy since the dependent list changes
      setFilters((prev) => ({ ...prev, [name]: event.target.value, visitedBy: '' }));
    } else {
      setFilters((prev) => ({ ...prev, [name]: event.target.value }));
    }
  }, []);

  const handleReset = () => {
    const defaults: ListFilters = {
      ...getStartEndOfMonth(),
      propertyId: '',
      visitType: '',
      userRole: '',
      visitedBy: '',
      visitStatus: '',
      searchText: '',
      managedByUserId: '',
    };
    setFilters(defaults);
    onReset();
  };

  return (
    <div className={`card mb-4 ${filtersApplied ? 'border border-primary border-2' : ''}`}>
      <div className="card-body">
        <div className="col-12 mb-3 d-flex justify-content-between align-items-center">
          <h5 className="m-0 text-dark">{t('common.filters')}</h5>
          {filtersApplied && (
            <small className="cursor-pointer text-primary" onClick={handleReset}>
              {t('common.clearFilters')}
            </small>
          )}
        </div>

        <div className="col-12">
          <div className="row g-3">
            {/* Row 1 */}
            <div className="col-md-2 col-sm-4">
              <Input
                type="date"
                id={VALIDATION_FIELD_MAPPING.startDate}
                name={VALIDATION_FIELD_MAPPING.startDate}
                placeholder={t('propertyVisits.list.pageTexts.filters.startDate')}
                value={filters.startDate}
                max={filters.endDate}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-2 col-sm-4">
              <Input
                type="date"
                id={VALIDATION_FIELD_MAPPING.endDate}
                name={VALIDATION_FIELD_MAPPING.endDate}
                placeholder={t('propertyVisits.list.pageTexts.filters.endDate')}
                value={filters.endDate}
                min={filters.startDate || undefined}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-4 col-sm-4">
              <Dropdown
                values={filters.propertyId ? [filters.propertyId] : []}
                options={(properties || []).map((p: any) => ({ label: p.name, value: p.id }))}
                allOptions={(allProperties || []).map((p: any) => ({ label: p.name, value: p.id }))}
                id={VALIDATION_FIELD_MAPPING.propertyId}
                name={VALIDATION_FIELD_MAPPING.propertyId}
                searchText={dropdownSearchData.propertyId}
                onSearch={onDropdownSearchChange}
                placeholder={t('propertyVisits.list.pageTexts.filters.selectProperty')}
                label={t('propertyVisits.list.pageTexts.filters.property')}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-4 col-sm-4">
              <Dropdown
                values={filters.visitType ? [filters.visitType] : []}
                options={(visitTypes || []).map((v) => ({ label: v.name, value: v.id }))}
                allOptions={(allVisitTypes || []).map((v) => ({ label: v.name, value: v.id }))}
                id={VALIDATION_FIELD_MAPPING.visitType}
                name={VALIDATION_FIELD_MAPPING.visitType}
                searchText={dropdownSearchData.visitType}
                onSearch={onDropdownSearchChange}
                placeholder={t('propertyVisits.list.pageTexts.filters.selectVisitType')}
                label={t('propertyVisits.list.pageTexts.filters.visitType')}
                onChange={handleInputChange}
              />
            </div>

            {/* Row 2 */}
            <div className="col-md-3 col-sm-4">
              <Dropdown
                values={filters.userRole ? [filters.userRole] : []}
                disabled={isInspector}
                options={(userRoles || []).map((r) => ({ label: r.name, value: r.id }))}
                allOptions={(allUserRoles || []).map((r) => ({ label: r.name, value: r.id }))}
                id={VALIDATION_FIELD_MAPPING.userRole}
                name={VALIDATION_FIELD_MAPPING.userRole}
                searchText={dropdownSearchData.userRole}
                onSearch={onDropdownSearchChange}
                placeholder={t('propertyVisits.list.pageTexts.filters.selectUserRole')}
                label={t('propertyVisits.list.pageTexts.filters.userRole')}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-3 col-sm-4">
              <Dropdown
                values={filters.visitedBy ? [filters.visitedBy] : []}
                disabled={isInspector || !filters.userRole}
                options={(visitedByOptions || []).map((u) => ({ label: u.name, value: u.id }))}
                allOptions={(allVisitedByOptions || []).map((u) => ({ label: u.name, value: u.id }))}
                id={VALIDATION_FIELD_MAPPING.visitedBy}
                name={VALIDATION_FIELD_MAPPING.visitedBy}
                searchText={dropdownSearchData.visitedBy}
                onSearch={onDropdownSearchChange}
                placeholder={t('propertyVisits.list.pageTexts.filters.selectVisitedBy')}
                label={t('propertyVisits.list.pageTexts.filters.visitedBy')}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-3 col-sm-4">
              <Dropdown
                values={filters.visitStatus ? [filters.visitStatus] : []}
                options={(visitStatuses || []).map((s) => ({ label: s.name, value: s.id }))}
                allOptions={(allVisitStatuses || []).map((s) => ({ label: s.name, value: s.id }))}
                id={VALIDATION_FIELD_MAPPING.visitStatus}
                name={VALIDATION_FIELD_MAPPING.visitStatus}
                searchText={dropdownSearchData.visitStatus}
                onSearch={onDropdownSearchChange}
                placeholder={t('propertyVisits.list.pageTexts.filters.selectVisitStatus')}
                label={t('propertyVisits.list.pageTexts.filters.visitStatus')}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-3 col-sm-4">
              <Dropdown
                values={filters.managedByUserId ? [filters.managedByUserId] : []}
                options={(managerOptions || []).map((m: any) => ({ label: m.name, value: String(m.id) }))}
                allOptions={(allManagerOptions || []).map((m: any) => ({ label: m.name, value: String(m.id) }))}
                id={VALIDATION_FIELD_MAPPING.managedByUserId}
                name={VALIDATION_FIELD_MAPPING.managedByUserId}
                searchText={dropdownSearchData.managedByUserId}
                onSearch={onDropdownSearchChange}
                placeholder={t('propertyVisits.list.pageTexts.filters.selectManagedBy')}
                label={t('propertyVisits.list.pageTexts.filters.managedBy')}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-3 col-sm-12">
              <Input
                type="search"
                id={VALIDATION_FIELD_MAPPING.searchText}
                name={VALIDATION_FIELD_MAPPING.searchText}
                placeholder={t('propertyVisits.list.pageTexts.filters.searchPlaceholder')}
                value={filters.searchText}
                onChange={handleInputChange}
                autoComplete="on"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Filters;
